import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Search, UserPlus, Phone, Mail, MapPin, Calendar, Briefcase } from 'lucide-react';

const RHEmpleados = () => {
  const [searchTerm, setSearchTerm] = useState('');

  // Datos de ejemplo de empleados
  const empleados = [
    {
      id: 1,
      nombre: "Juan Pérez García",
      puesto: "Gerente de Operaciones",
      departamento: "Operaciones",
      telefono: "664-123-4567",
      email: "juan.perez@titsa.com",
      fechaIngreso: "2018-03-15",
      salario: "$35,000",
      status: "Activo",
      ubicacion: "Tijuana, BC"
    },
    {
      id: 2,
      nombre: "María López Hernández",
      puesto: "Supervisora de Almacén",
      departamento: "Logística",
      telefono: "664-234-5678",
      email: "maria.lopez@titsa.com",
      fechaIngreso: "2019-07-20",
      salario: "$28,000",
      status: "Activo",
      ubicacion: "Tijuana, BC"
    },
    {
      id: 3,
      nombre: "Carlos Ramírez Soto",
      puesto: "Mecánico A",
      departamento: "Mantenimiento",
      telefono: "664-345-6789",
      email: "carlos.ramirez@titsa.com",
      fechaIngreso: "2020-01-10",
      salario: "$22,000",
      status: "Activo",
      ubicacion: "Tijuana, BC"
    },
    {
      id: 4,
      nombre: "Ana Martínez Torres",
      puesto: "Analista de RRHH",
      departamento: "Recursos Humanos",
      telefono: "664-456-7890",
      email: "ana.martinez@titsa.com",
      fechaIngreso: "2021-05-12",
      salario: "$25,000",
      status: "Activo",
      ubicacion: "Tijuana, BC"
    },
    {
      id: 5,
      nombre: "Roberto González Cruz",
      puesto: "Operador de Tractocamión",
      departamento: "Transporte",
      telefono: "664-567-8901",
      email: "roberto.gonzalez@titsa.com",
      fechaIngreso: "2017-09-05",
      salario: "$30,000",
      status: "Activo",
      ubicacion: "Tijuana, BC"
    },
    {
      id: 6,
      nombre: "Laura Sánchez Díaz",
      puesto: "Asistente Administrativa",
      departamento: "Administración",
      telefono: "664-678-9012",
      email: "laura.sanchez@titsa.com",
      fechaIngreso: "2022-02-28",
      salario: "$18,000",
      status: "Vacaciones",
      ubicacion: "Tijuana, BC"
    }
  ];

  const filteredEmpleados = empleados.filter(emp =>
    emp.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.puesto.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.departamento.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/recursos-humanos">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg transition"
              >
                <ArrowLeft className="w-6 h-6 text-gray-700" />
              </motion.button>
            </Link>
            <h1 className="text-4xl font-bold text-gray-800">Base de Datos de Empleados</h1>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="flex items-center gap-2 bg-blue-500 text-white px-6 py-3 rounded-lg shadow-lg hover:bg-blue-600 transition"
          >
            <UserPlus className="w-5 h-5" />
            Nuevo Empleado
          </motion.button>
        </div>

        {/* Barra de búsqueda */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar por nombre, puesto o departamento..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Resumen de estadísticas */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-md p-4">
            <p className="text-gray-500 text-sm">Total Empleados</p>
            <p className="text-3xl font-bold text-blue-600">{empleados.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4">
            <p className="text-gray-500 text-sm">Activos</p>
            <p className="text-3xl font-bold text-green-600">{empleados.filter(e => e.status === 'Activo').length}</p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4">
            <p className="text-gray-500 text-sm">En Vacaciones</p>
            <p className="text-3xl font-bold text-orange-600">{empleados.filter(e => e.status === 'Vacaciones').length}</p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4">
            <p className="text-gray-500 text-sm">Departamentos</p>
            <p className="text-3xl font-bold text-purple-600">{new Set(empleados.map(e => e.departamento)).size}</p>
          </div>
        </div>

        {/* Lista de empleados */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredEmpleados.map((empleado, index) => (
            <motion.div
              key={empleado.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-xl shadow-lg p-6 hover:shadow-2xl transition-all duration-300"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                  {empleado.nombre.split(' ').map(n => n[0]).join('').substring(0, 2)}
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  empleado.status === 'Activo' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                }`}>
                  {empleado.status}
                </span>
              </div>
              
              <h3 className="text-xl font-bold text-gray-800 mb-2">{empleado.nombre}</h3>
              
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-blue-500" />
                  <span className="font-semibold">{empleado.puesto}</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <span>{empleado.departamento}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <span>{empleado.telefono}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <span className="truncate">{empleado.email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span>Ingreso: {new Date(empleado.fechaIngreso).toLocaleDateString('es-MX')}</span>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Salario</span>
                  <span className="text-lg font-bold text-blue-600">{empleado.salario}</span>
                </div>
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full mt-4 bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 transition"
              >
                Ver Detalles
              </motion.button>
            </motion.div>
          ))}
        </div>

        {filteredEmpleados.length === 0 && (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <p className="text-gray-500 text-lg">No se encontraron empleados con ese criterio de búsqueda.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RHEmpleados;
